import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Bell, Heart, MessageCircle, ShieldCheck, Eye, Check } from 'lucide-react';
import api from '../lib/api';

const iconMap = {
  interest_received: Heart,
  interest_accepted: Heart,
  profile_viewed: Eye,
  kyc_approved: ShieldCheck,
  message: MessageCircle,
};

export default function NotificationCenter() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const res = await api.get('/notifications');
      const notifs = res.data.notifications || [];
      setNotifications(notifs);
      setUnreadCount(notifs.filter(n => !n.read).length);
    } catch (e) {
      console.error(e);
    }
  }, [user]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 15000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAllRead = async () => {
    try {
      await api.put('/notifications/read');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (e) {
      console.error(e);
    }
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  if (!user) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="relative" data-testid="notification-bell">
          <Bell className="w-5 h-5 text-[#2D2424]" />
          {unreadCount > 0 && (
            <Badge className="absolute -top-1 -right-1 w-5 h-5 p-0 flex items-center justify-center bg-[#D9381E] text-white text-[10px] rounded-full border-2 border-white">
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 rounded-xl" data-testid="notification-panel">
        <div className="flex items-center justify-between p-3 border-b border-[#E7E5E4]">
          <span className="text-sm font-semibold text-[#2D2424]">Notifications</span>
          {unreadCount > 0 && (
            <Button variant="ghost" size="sm" onClick={markAllRead} className="text-xs text-[#D9381E] h-7" data-testid="mark-all-read-btn">
              <Check className="w-3 h-3 mr-1" /> Mark all read
            </Button>
          )}
        </div>
        <ScrollArea className="max-h-80">
          {notifications.length > 0 ? notifications.slice(0, 20).map(n => {
            const Icon = iconMap[n.type] || Bell;
            return (
              <div key={n.id} className={`px-3 py-3 border-b border-[#E7E5E4]/50 hover:bg-[#F9F5F0] transition-colors ${!n.read ? 'bg-[#D9381E]/5' : ''}`}>
                <div className="flex items-start gap-2.5">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${!n.read ? 'bg-[#D9381E]/10' : 'bg-[#F9F5F0]'}`}>
                    <Icon className={`w-4 h-4 ${!n.read ? 'text-[#D9381E]' : 'text-[#78716C]'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm leading-tight ${!n.read ? 'font-medium text-[#2D2424]' : 'text-[#78716C]'}`}>{n.title}</p>
                    <p className="text-[10px] text-[#78716C] mt-1">{timeAgo(n.created_at)}</p>
                  </div>
                  {!n.read && <div className="w-2 h-2 bg-[#D9381E] rounded-full flex-shrink-0 mt-1.5" />}
                </div>
              </div>
            );
          }) : (
            <div className="p-8 text-center">
              <Bell className="w-8 h-8 text-[#E7E5E4] mx-auto mb-2" />
              <p className="text-xs text-[#78716C]">No notifications yet</p>
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
